// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductShareLink, { ProductShareLinkStyles } from "@insite/content-library/Components/ProductShareLink";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PdpProductPriceAndActions, {
    PdpProductPriceAndActionsStyle,
} from "@rpsc/App/Components/Product/PdpProductPriceAndActions";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import { clipboardText } from "@rpsc/App/Widgets/DesignSystem/Utilities";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import React, { FC } from "react";
import { connect } from "react-redux";

export interface ProductDetailsActionsStyles {
    productGridContainerStyles?: PdpProductPriceAndActionsStyle;
    wrapper?: InjectableCss;
    shareContainer?: InjectableCss;
    actionsWrapper?: InjectableCss;
    printLink?: LinkPresentationProps;
    shareLink?: ProductShareLinkStyles;
    copyLink?: LinkPresentationProps;
}

export const productDetailsActionsStyles: ProductDetailsActionsStyles = {};

const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

const styles = productDetailsActionsStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const ProductDetailsActions: FC<Props> = () => {
    const toaster = useToaster();
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="product-details-actions">
            {/* Email & Print */}
            <StyledWrapper {...styles.shareContainer}>
                <ProductShareLink extendedStyles={styles.shareLink} text={translate("Email")} />
                <Link {...styles.printLink} onClick={openPrintDialog} aria-label={translate("Print")}>
                    {translate("Print")}
                </Link>
                <Link
                    {...styles.copyLink}
                    aria-label={translate("Copy Link")}
                    onClick={() => {
                        const isCopied = clipboardText(window.location.href);
                        toaster.addToast({
                            body: isCopied ? (
                                <Typography>{translate("Page link copied")}</Typography>
                            ) : (
                                <Typography>{translate("Copy page link failed!")}</Typography>
                            ),
                            messageType: isCopied ? "success" : "danger",
                        });
                    }}
                >
                    {translate("Copy Link")}
                </Link>
            </StyledWrapper>

            {/* Actions box */}
            <StyledBox {...styles.actionsWrapper}>
                <PdpProductPriceAndActions extendedStyles={styles.productGridContainerStyles} />
            </StyledBox>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductDetailsActions),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
